/**
 * Fraud Report Export Utility
 *
 * Generates downloadable fraud analysis reports in text/markdown format
 * Includes complete Phase 1/2/3 breakdown with evidence and recommendations
 */

interface Phase1RedFlags {
  badEventQuickSwitchClean: boolean;
  rapidBackAndForth: boolean;
  ghostCarrierViolation: boolean;
  dumpAndReturnAfterIdle: boolean;
  shellCarrierOnlyBadEvents: boolean;
}

interface Phase2Indicators {
  relatedCarriers: boolean;
  sameOwner: boolean;
  sameAddress: boolean;
  samePhone: boolean;
  recentlyCreatedCarriers: boolean;
}

interface Phase3Whitelist {
  longGaps: boolean;
  legitimateSale: boolean;
  linearProgression: boolean;
  rentalUnit: boolean;
  postRevocationDispersal: boolean;
}

interface DecisionMatrix {
  condition: string;
  action: string;
}

interface CarrierStatus {
  dotNumber: number;
  carrierName: string;
  status: string;
  statusCode: string;
}

interface CarrierSwitch {
  fromDotNumber: number;
  fromCarrierName: string;
  lastSeenAt: string;
  toDotNumber: number;
  toCarrierName: string;
  firstSeenAt: string;
  daysBetween: number;
  suspicious: boolean;
  suspicionReasons: string[];
}

interface CarrierPeriod {
  dotNumber: number;
  carrierName: string;
  startDate: string;
  endDate: string;
  eventCount: number;
}

interface FraudAnalysisData {
  level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  reason: string;
  details: string[];
  fraudDetected: boolean;
  phase1RedFlags: Phase1RedFlags;
  phase2Indicators: Phase2Indicators;
  phase3Whitelist: Phase3Whitelist;
  decisionMatrix: DecisionMatrix;
  carrierStatuses?: CarrierStatus[];
}

interface TimelineData {
  vin: string;
  totalEvents: number;
  uniqueCarriers: number;
  switchCount: number;
  suspiciousSwitches: number;
  switches: CarrierSwitch[];
  carrierPeriods: CarrierPeriod[];
  advancedFraudAnalysis?: FraudAnalysisData;
  isRentalUnit?: boolean;
  rentalCompanies?: Array<{ dotNumber: string; companyName: string }>;
}

/**
 * Format date consistently
 */
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Generate phase 1 red flags section
 */
const generatePhase1Section = (phase1: Phase1RedFlags): string => {
  const flags = [
    { key: 'badEventQuickSwitchClean', label: 'Bad Event -> Quick Switch -> Clean', description: 'Vehicle receives a violation/crash, switches carriers within 60 days, then gets a clean inspection' },
    { key: 'rapidBackAndForth', label: 'Rapid Back-and-Forth Pattern', description: 'Vehicle ping-pongs between the same carriers multiple times within 18 months' },
    { key: 'ghostCarrierViolation', label: 'Ghost Carrier Violation', description: 'Bad event on a carrier that was inactive/OOS for >90 days' },
    { key: 'dumpAndReturnAfterIdle', label: 'Dump-and-Return After Idle', description: 'Vehicle moves to shell carrier after violation, then returns to original carrier' },
    { key: 'shellCarrierOnlyBadEvents', label: 'Shell Carrier Only Bad Events', description: 'Carrier has only 1-2 events for this VIN, all bad (100% bad rate)' },
  ];

  let section = '## Phase 1: Critical Red Flags\n\n';
  section += 'Critical patterns that indicate active fraud. Any flag warrants investigation.\n\n';

  flags.forEach((flag) => {
    const detected = phase1[flag.key as keyof Phase1RedFlags];
    const status = detected ? '[X] DETECTED' : '[ ] Not detected';
    section += `${status} - ${flag.label}\n`;
    if (detected) {
      section += `   Description: ${flag.description}\n`;
    }
  });

  const count = Object.values(phase1).filter(Boolean).length;
  section += `\nTotal Phase 1 Red Flags: ${count}/5\n`;

  return section;
};

/**
 * Generate phase 2 indicators section
 */
const generatePhase2Section = (phase2: Phase2Indicators): string => {
  const indicators = [
    { key: 'relatedCarriers', label: 'Related Carriers Detected', description: 'Carriers have detectable business relationships' },
    { key: 'sameOwner', label: 'Same Owner', description: 'Carriers share the same owner or legal name' },
    { key: 'sameAddress', label: 'Same Address', description: 'Carriers operate from the same physical address' },
    { key: 'samePhone', label: 'Same Phone Number', description: 'Carriers share the same phone number' },
    { key: 'recentlyCreatedCarriers', label: 'Recently Created Carriers', description: 'Multiple carriers created within the last 18 months' },
  ];

  let section = '## Phase 2: Supporting Evidence\n\n';
  section += 'Carrier relationship indicators that strengthen fraud cases when combined with Phase 1 flags.\n\n';

  indicators.forEach((indicator) => {
    const detected = phase2[indicator.key as keyof Phase2Indicators];
    const status = detected ? '[X] DETECTED' : '[ ] Not detected';
    section += `${status} - ${indicator.label}\n`;
    if (detected) {
      section += `   Description: ${indicator.description}\n`;
    }
  });

  const count = Object.values(phase2).filter(Boolean).length;
  section += `\nTotal Phase 2 Indicators: ${count}/5\n`;

  return section;
};

/**
 * Generate phase 3 whitelist section
 */
const generatePhase3Section = (phase3: Phase3Whitelist): string => {
  const patterns = [
    { key: 'longGaps', label: 'Long Gaps Between Switches (>180 days)', description: 'All carrier switches have gaps >180 days (legitimate sales pattern)' },
    { key: 'legitimateSale', label: 'Legitimate Sale Pattern', description: 'Single carrier transfer with appropriate gap and dwell time' },
    { key: 'linearProgression', label: 'Linear Progression (No Returns)', description: 'Vehicle moves forward through carriers without returning (leasing pattern)' },
    { key: 'rentalUnit', label: 'Rental Unit Identified', description: 'Vehicle is a rental unit - transfers are normal business operations' },
    { key: 'postRevocationDispersal', label: 'Post-Revocation Dispersal', description: 'Switch after carrier revocation to unrelated active carrier' },
  ];

  let section = '## Phase 3: Legitimate Patterns (Whitelisting)\n\n';
  section += 'Patterns that indicate legitimate business operations and reduce fraud risk.\n\n';

  patterns.forEach((pattern) => {
    const detected = phase3[pattern.key as keyof Phase3Whitelist];
    const status = detected ? '[X] DETECTED' : '[ ] Not detected';
    section += `${status} - ${pattern.label}\n`;
    if (detected) {
      section += `   Description: ${pattern.description}\n`;
    }
  });

  const count = Object.values(phase3).filter(Boolean).length;
  section += `\nTotal Phase 3 Whitelist Patterns: ${count}/5\n`;

  return section;
};

/**
 * Generate carrier history section
 */
const generateCarrierHistorySection = (
  carrierPeriods: CarrierPeriod[],
  carrierStatuses?: CarrierStatus[]
): string => {
  let section = '## Carrier History\n\n';

  if (carrierPeriods.length === 0) {
    section += 'No carrier history available.\n';
    return section;
  }

  section += `Total Carriers: ${carrierPeriods.length}\n\n`;

  carrierPeriods.forEach((period, idx) => {
    const status = carrierStatuses?.find((s) => s.dotNumber === period.dotNumber);
    section += `### Carrier ${idx + 1}: ${period.carrierName}\n`;
    section += `- DOT Number: ${period.dotNumber}\n`;
    section += `- Period: ${formatDate(period.startDate)} to ${formatDate(period.endDate)}\n`;
    section += `- Event Count: ${period.eventCount}\n`;
    if (status) {
      section += `- Current Status: ${status.status}\n`;
    }
    section += '\n';
  });

  return section;
};

/**
 * Generate switches section
 */
const generateSwitchesSection = (switches: CarrierSwitch[]): string => {
  let section = '## Carrier Switches Analysis\n\n';

  if (switches.length === 0) {
    section += 'No carrier switches detected - single carrier history.\n';
    return section;
  }

  const suspiciousCount = switches.filter((s) => s.suspicious).length;
  section += `Total Switches: ${switches.length}\n`;
  section += `Suspicious Switches: ${suspiciousCount}\n\n`;

  switches.forEach((sw, idx) => {
    const switchStatus = sw.suspicious ? '[SUSPICIOUS]' : '[NORMAL]';
    section += `### Switch ${idx + 1} ${switchStatus}\n`;
    section += `- From: ${sw.fromCarrierName} (DOT ${sw.fromDotNumber})\n`;
    section += `- To: ${sw.toCarrierName} (DOT ${sw.toDotNumber})\n`;
    section += `- Last seen at old carrier: ${formatDate(sw.lastSeenAt)}\n`;
    section += `- First seen at new carrier: ${formatDate(sw.firstSeenAt)}\n`;
    section += `- Days between: ${sw.daysBetween}\n`;

    if (sw.suspicious && sw.suspicionReasons.length > 0) {
      section += `- Suspicion Reasons:\n`;
      sw.suspicionReasons.forEach((reason) => {
        section += `  * ${reason}\n`;
      });
    }
    section += '\n';
  });

  return section;
};

/**
 * Export fraud analysis report as text file
 */
export const exportFraudReport = (
  vin: string,
  timelineData: TimelineData
): void => {
  const analysis = timelineData.advancedFraudAnalysis;
  const reportDate = new Date().toISOString().split('T')[0];

  // If no fraud analysis available, show basic report
  if (!analysis) {
    const basicReport = `# VIN FRAUD ANALYSIS REPORT
================================================================================

Report Generated: ${new Date().toLocaleString()}
VIN: ${vin}

================================================================================
## EXECUTIVE SUMMARY
================================================================================

Risk Level: N/A
Fraud Detected: NO ADVANCED ANALYSIS AVAILABLE

Quick Stats:
- Total Events: ${timelineData.totalEvents}
- Unique Carriers: ${timelineData.uniqueCarriers}
- Carrier Switches: ${timelineData.switchCount}
- Suspicious Switches: ${timelineData.suspiciousSwitches}

Note: Advanced fraud analysis (Phase 1/2/3) data not available for this VIN.

================================================================================
${generateCarrierHistorySection(timelineData.carrierPeriods)}
================================================================================
${generateSwitchesSection(timelineData.switches)}
================================================================================
END OF REPORT
================================================================================
`;
    const blob = new Blob([basicReport], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fraud-report-${vin}-${reportDate}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return;
  }

  let report = `# VIN FRAUD ANALYSIS REPORT
================================================================================

Report Generated: ${new Date().toLocaleString()}
VIN: ${vin}

================================================================================
## EXECUTIVE SUMMARY
================================================================================

Risk Level: ${analysis.level}
Fraud Detected: ${analysis.fraudDetected ? 'YES' : 'NO'}

Primary Finding: ${analysis.reason}

Decision Matrix:
- Condition: ${analysis.decisionMatrix.condition}
- Recommended Action: ${analysis.decisionMatrix.action}

Quick Stats:
- Total Events: ${timelineData.totalEvents}
- Unique Carriers: ${timelineData.uniqueCarriers}
- Carrier Switches: ${timelineData.switchCount}
- Suspicious Switches: ${timelineData.suspiciousSwitches}

`;

  // Rental unit notice
  if (timelineData.isRentalUnit) {
    report += `================================================================================
## RENTAL UNIT NOTICE
================================================================================

This VIN has been identified as a RENTAL UNIT.
Transfers between carriers are expected for rental vehicles and do not
necessarily indicate fraudulent activity.

`;
    if (timelineData.rentalCompanies && timelineData.rentalCompanies.length > 0) {
      report += `Associated Rental Companies:\n`;
      timelineData.rentalCompanies.forEach((company) => {
        report += `- ${company.companyName} (DOT ${company.dotNumber})\n`;
      });
      report += '\n';
    }
  }

  report += `================================================================================
${generatePhase1Section(analysis.phase1RedFlags)}
================================================================================
${generatePhase2Section(analysis.phase2Indicators)}
================================================================================
${generatePhase3Section(analysis.phase3Whitelist)}
================================================================================
${generateCarrierHistorySection(timelineData.carrierPeriods, analysis.carrierStatuses)}
================================================================================
${generateSwitchesSection(timelineData.switches)}
================================================================================
## DETAILED EVIDENCE
================================================================================

`;

  if (analysis.details && analysis.details.length > 0) {
    analysis.details.forEach((detail, idx) => {
      report += `${idx + 1}. ${detail}\n`;
    });
  } else {
    report += 'No additional evidence details available.\n';
  }

  report += `
================================================================================
## CARRIER STATUS REFERENCE
================================================================================

`;

  if (analysis.carrierStatuses && analysis.carrierStatuses.length > 0) {
    analysis.carrierStatuses.forEach((carrier) => {
      report += `- DOT ${carrier.dotNumber}: ${carrier.carrierName} - ${carrier.status}\n`;
    });
  } else {
    report += 'No carrier status information available.\n';
  }

  report += `
================================================================================
## DISCLAIMER
================================================================================

This report was generated automatically by the Transportation Safety Platform
fraud detection system. It should be used as a tool to assist in fraud
investigation and is not a definitive determination of fraud. All findings
should be verified through independent investigation.

Report generated using Advanced Fraud Detection Algorithm v1.0
Phase 1: Critical Red Flags (5 patterns)
Phase 2: Supporting Indicators (5 patterns)
Phase 3: Whitelisting Patterns (5 patterns)

================================================================================
END OF REPORT
================================================================================
`;

  // Create and trigger download
  const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `fraud-report-${vin}-${reportDate}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export fraud analysis as JSON
 */
export const exportFraudReportJSON = (
  vin: string,
  timelineData: TimelineData
): void => {
  const reportDate = new Date().toISOString().split('T')[0];
  const analysis = timelineData.advancedFraudAnalysis;

  const reportData = {
    reportGeneratedAt: new Date().toISOString(),
    vin,
    summary: analysis ? {
      riskLevel: analysis.level,
      fraudDetected: analysis.fraudDetected,
      reason: analysis.reason,
      decisionMatrix: analysis.decisionMatrix,
    } : {
      riskLevel: 'N/A',
      fraudDetected: false,
      reason: 'Advanced analysis not available',
      decisionMatrix: { condition: 'N/A', action: 'N/A' },
    },
    stats: {
      totalEvents: timelineData.totalEvents,
      uniqueCarriers: timelineData.uniqueCarriers,
      switchCount: timelineData.switchCount,
      suspiciousSwitches: timelineData.suspiciousSwitches,
    },
    isRentalUnit: timelineData.isRentalUnit || false,
    rentalCompanies: timelineData.rentalCompanies || [],
    phase1RedFlags: analysis?.phase1RedFlags || null,
    phase2Indicators: analysis?.phase2Indicators || null,
    phase3Whitelist: analysis?.phase3Whitelist || null,
    carrierPeriods: timelineData.carrierPeriods,
    switches: timelineData.switches,
    details: analysis?.details || [],
    carrierStatuses: analysis?.carrierStatuses || [],
  };

  const blob = new Blob([JSON.stringify(reportData, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `fraud-report-${vin}-${reportDate}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default exportFraudReport;
